import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { Select } from "radix-ui";
import { cn } from "@/lib/utils";

const ROLES = [
  { id: "member", name: "Member" },
  { id: "admin", name: "Admin" },
] as const;

/**
 * The role picker beside the invite field. A Radix select instead of the browser's native one so
 * it matches the input next to it (same height, border and radius) and its menu never leaves the
 * screen.
 */
export function RoleSelect({
  value,
  onValueChange,
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}) {
  return (
    <Select.Root value={value} onValueChange={onValueChange}>
      <Select.Trigger
        aria-label="Role"
        className={cn(
          "focus-visible:ring-ring/50 border-border bg-background hover:bg-accent data-[state=open]:bg-accent flex h-8 w-28 shrink-0 items-center justify-between gap-1.5 rounded-lg border px-2.5 text-xs transition-colors outline-none focus-visible:ring-2",
          className,
        )}
      >
        <Select.Value />
        <Select.Icon>
          <ChevronDownIcon className="size-3.5 opacity-50" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          align="end"
          sideOffset={6}
          collisionPadding={8}
          className="bg-popover/95 text-popover-foreground data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 z-50 w-(--radix-select-trigger-width) min-w-28 overflow-hidden rounded-xl border p-1 shadow-lg backdrop-blur-sm"
        >
          <Select.Viewport>
            {ROLES.map((role) => (
              <Select.Item
                key={role.id}
                value={role.id}
                className="focus:bg-accent focus:text-accent-foreground relative flex cursor-pointer items-center rounded-lg py-1.5 ps-2.5 pe-7 text-xs outline-none select-none"
              >
                <Select.ItemText>{role.name}</Select.ItemText>
                <Select.ItemIndicator className="absolute end-2 flex items-center">
                  <CheckIcon className="size-3.5" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
